/*
 * The one place the app calls fetch().
 *
 * Every request made through here is same-origin, sends the session cookie,
 * carries the CSRF token on anything that is not a GET (Flask-WTF's
 * CSRFProtect rejects it otherwise -- see app/__init__.py), and has every
 * path segment URL-encoded. Non-2xx responses reject with an ApiError that
 * carries the status and the decoded body, so a caller can show the server's
 * message instead of choking on r.json() of an HTML error page.
 *
 *   api.get('/getuserstrategies')                          -> parsed JSON
 *   api.get('/fetchassets', { search: term, exchange })    -> ?search=..&exchange=..
 *   api.get(api.path('/history', asset, timeframe, ex))    -> /history/BTC%2FUSDT/1h/binance
 *   api.post('/layouts', { name })                         -> JSON body, parsed JSON back
 *   api.post('/scanner/api/scans', formData)               -> FormData passes through
 *   api.raw('/aireportv2', { method: 'POST', body })       -> the Response itself
 *                                                             (streaming, custom status handling)
 *
 * Only absolute paths ('/...') are accepted: a full URL to another origin is
 * a bug, not a request, and would leak the session cookie / CSRF token.
 */

export class ApiError extends Error {
  constructor(status, url, payload, method = 'GET') {
    const detail = payload && typeof payload === 'object'
      // the readable sentence first: several routes send a machine code in `error`
      // and the sentence in `message`
      ? ((typeof payload.message === 'string' && payload.message) || payload.error || payload.detail)
      : (typeof payload === 'string' && payload.length < 200 && payload[0] !== '<' ? payload : '');
    // The message names the request: a bare "Internal Server Error" body in a
    // minified stack trace says nothing about which endpoint failed.
    super(`${method} ${url} failed (${status})` + (detail ? `: ${detail}` : ''));
    this.name = 'ApiError';
    this.status = status;
    this.url = url;
    this.method = method;
    this.payload = payload;
    // What the server said, if it said anything readable; for the toast.
    this.detail = detail ? String(detail) : '';
  }
}

/* Encode each segment and join with '/': api.path('/history', 'BTC/USDT', '1h')
   -> '/history/BTC%2FUSDT/1h'. Segments are never trusted to be pre-encoded. */
export function path(base, ...segments) {
  return base.replace(/\/+$/, '') + segments.map((s) => '/' + encodeURIComponent(String(s))).join('');
}

function query(params) {
  if (!params) return '';
  const q = new URLSearchParams();
  Object.keys(params).forEach((k) => {
    const v = params[k];
    if (v !== undefined && v !== null) q.append(k, String(v));
  });
  const s = q.toString();
  return s ? '?' + s : '';
}

let csrfToken = null;
function csrf() {
  if (csrfToken === null) {
    const meta = document.querySelector('meta[name="csrf-token"]');
    csrfToken = meta ? meta.getAttribute('content') || '' : '';
  }
  return csrfToken;
}

function assertSameOrigin(url) {
  if (typeof url !== 'string' || url[0] !== '/' || url[1] === '/') {
    throw new Error('api: only same-origin absolute paths are allowed, got ' + url);
  }
}

/*
 * Decode a body as JSON when it IS JSON, whichever way the server labels it.
 *
 * Several views.py routes return a bare string (pandas' `to_json()`, or a body
 * relayed from crpytview), which Flask serves as `text/html`. Trusting the
 * header there handed callers a string where they expected an array. The
 * header is honoured first; a text body is only parsed when it actually
 * begins as a JSON object or array, so an HTML error page still arrives as
 * text for ApiError to report.
 */
async function decode(response) {
  const type = response.headers.get('content-type') || '';
  if (type.indexOf('application/json') > -1) {
    try { return await response.json(); } catch (e) { return null; }
  }
  let text;
  try { text = await response.text(); } catch (e) { return null; }
  if (typeof text === 'string') {
    const head = text.trimStart()[0];
    if (head === '{' || head === '[') {
      try { return JSON.parse(text); } catch (e) { /* not JSON after all */ }
    }
  }
  return text;
}

/* The Response, with cookie / CSRF / origin handled. Use when you need the
   status or the stream yourself. `body` objects are JSON-encoded unless they
   are FormData, URLSearchParams, Blob or a string. */
export async function raw(url, { method = 'GET', body, headers, params, signal } = {}) {
  assertSameOrigin(url);
  const h = Object.assign({ Accept: 'application/json' }, headers || {});
  const m = method.toUpperCase();
  let payload = body;
  if (payload !== undefined && payload !== null
      && !(payload instanceof FormData) && !(payload instanceof URLSearchParams)
      && !(payload instanceof Blob) && typeof payload !== 'string') {
    payload = JSON.stringify(payload);
    if (!h['Content-Type']) h['Content-Type'] = 'application/json';
  }
  if (m !== 'GET' && m !== 'HEAD') h['X-CSRFToken'] = csrf();
  return fetch(url + query(params), {
    method: m,
    headers: h,
    body: payload,
    credentials: 'same-origin',
    signal,
  });
}

/* Did the server answer by sending us to the login page? That is what a lapsed
   session looks like to fetch(): the redirect is followed silently and the
   login page's HTML comes back as a 200, which a caller would then read as an
   empty result (the News panel showed "No news yet"). */
function wasSentToLogin(response) {
  if (!response.redirected) return false;
  try {
    return /^\/login(\/|$)/.test(new URL(response.url).pathname);
  } catch (e) {
    return false;
  }
}

/* Parsed JSON on 2xx; ApiError otherwise. A lapsed session is a 401. */
async function json(url, opts) {
  const response = await raw(url, opts);
  if (wasSentToLogin(response)) {
    throw new ApiError(401, url, { error: 'session_expired', message: 'Your session has ended.' }, (opts && opts.method) || 'GET');
  }
  const data = await decode(response);
  if (!response.ok) throw new ApiError(response.status, url, data, (opts && opts.method) || 'GET');
  return data;
}

export const api = {
  path,
  raw,
  get(url, params) { return json(url, { params }); },
  post(url, body, opts) { return json(url, Object.assign({}, opts, { method: 'POST', body })); },
  /* Show an ApiError (or anything else that was thrown) in the toast region,
     if the page has one. Returns the message it showed. */
  notify(err, fallback) {
    const msg = (err instanceof ApiError && err.detail) || fallback || 'Something went wrong.';
    try {
      const store = window.Alpine && window.Alpine.store('toast');
      if (store) store.show(msg, { tone: 'error' });
    } catch (e) { /* toast not mounted */ }
    return msg;
  },
};

export default api;
